import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/index';
import { apiGetMineInfo, apiGetPage, apiGetCustom, apiGetApplicationDetail } from '../request/api'
import { imageUrl, zeusApp } from '../request/index'
import { LogoutOutlined } from '@ant-design/icons';
import '../assets/css/header.css'

interface OrderItem {
    name: string;
    url: string;
}

interface ApiPageRow {
    mainLand: string;
    siteName: string;
    siteLanguage: string;
    siteCode: string;
}

interface TransformedLanguageList {
    areaName: string;
    items: {
        name: string;
        siteCode: string;
        languageList: { label: string; value: string }[];
        isClose: boolean;
    }[];
}

const Header: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const themeColor = useSelector((state: RootState) => state.themeColor.value);
    const [isLanguageVisible, setIsLanguageVisible] = useState<boolean>(false);
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);
    const dispatch = useDispatch();
    const [hoveredIndex, setHoveredIndex] = useState<{ regionIndex: number | null, countryIndex: number | null }>({
        regionIndex: null,
        countryIndex: null
    });
    const [transformedList, setTransformedList] = useState<TransformedLanguageList[]>([]);
    const [loginState, setLoginState] = useState(0);
    const [isLanguageHover, setIsLanguageHover] = useState<boolean>(false);
    const [isUserHover, setIsUserHover] = useState<boolean>(false);
    const [isUserIconHovered, setIsUserIconHovered] = useState(false);
    const [navList, setNavList] = useState<OrderItem[]>([]);
    const intervalRef = React.useRef<NodeJS.Timeout | null>(null);
    const [selectedLanguageDisplay, setSelectedLanguageDisplay] = useState<string>('**-**');
    const languageCode = localStorage.getItem('siteLanguage') || 'zh';

    const [hoveredLanguageItem, setHoveredLanguageItem] = useState<{
        regionIndex: number | null;
        countryIndex: number | null;
        itemIndex: number | null;
    }>({
        regionIndex: null,
        countryIndex: null,
        itemIndex: null
    });

    useEffect(() => {
        fetchUserInfo();
        
        // 获取应用logo和icon
        apiGetApplicationDetail('APPLICATION_LOGO').then(res => {
            localStorage.setItem('logoImage', res.data.data)
        });
        
        apiGetApplicationDetail('APPLICATION_ICON').then(res => {
            localStorage.setItem('iconImage', res.data.data)
            const icon = localStorage.getItem('iconImage')
            const faviconUrl = `${imageUrl}${icon}?zeus-app=${zeusApp}`;
            let link: HTMLLinkElement | null = document.querySelector("link[rel*='icon']");
            if (!link) {
                link = document.createElement("link");
                link.rel = "icon";
                document.head.appendChild(link);
            }
            link.href = faviconUrl;
        });
        
        // 设置循环定时器，每隔一段时间获取用户信息
        intervalRef.current = setInterval(() => {
            fetchUserInfo();
        }, 60000); // 每 60 秒执行一次

        // 清理定时器，避免组件卸载时内存泄漏
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [dispatch]);

    const fetchUserInfo = () => {
        apiGetMineInfo().then(res => {
            if (res.data.data !== null) {
                setLoginState(1); // 如果 token 有效，设置登录状态
            } else {
                setLoginState(0); // 如果 token 无效，设置未登录状态
            }
        }).catch(err => {
            console.error('Error fetching user info:', err);
            setLoginState(0); // 请求失败时，也设置为未登录状态
        });
    };

    const handleMouseEnter = (index: number) => {
        setHoverIndex(index);
    };

    const handleMouseLeave = () => {
        setHoverIndex(null);
    };

    const handleNavigation = (path: string) => {
        navigate(path, { state: { resetView: true } });
    };

    const toMine = () => {
        if (loginState) {
            navigate('/mine'); // 如果有 token，导航到 /mine
        } else {
            navigate('/login'); // 如果没有 token，导航到 /login
        }
    };

    const showLanguage = () => {
        setIsLanguageVisible(!isLanguageVisible);
    };

    // 获取用户头像ID
    const getUserAvatarId = () => {
        // 从localStorage获取保存的头像ID
        const headImg = localStorage.getItem('headImg');
        return headImg;
    };

    // 渲染用户头像或图标
    const renderUserAvatarOrIcon = () => {
        if (loginState === 1) {
            // 已登录：显示头像
            const headImg = getUserAvatarId();
            if (headImg) {
                // 拼接完整头像URL
                return (
                    <img 
                        src={`${imageUrl}${headImg}?zeus-app=${zeusApp}`} 
                        alt="user avatar" 
                        style={{ 
                            width: '32px', 
                            height: '32px', 
                            borderRadius: '50%',
                            objectFit: 'cover',
                            cursor: 'pointer'
                        }}
                        onClick={() => {
                            toMine();
                            setIsLanguageVisible(false);
                        }}
                        onMouseEnter={() => {
                            setIsUserIconHovered(true);
                            setIsUserHover(false);
                        }}
                        onMouseLeave={() => {
                            if (!isUserHover) {
                                setIsUserIconHovered(false);
                            }
                        }}
                    />
                );
            } else {
                // 如果没有头像ID，显示默认图标
                return renderUserIcon();
            }
        } else {
            // 未登录：显示用户图标
            return renderUserIcon();
        }
    };

    // 渲染用户图标
    const renderUserIcon = () => {
        return (
            <i
                className="iconfont icon-User"
                onClick={() => {
                    toMine();
                    setIsLanguageVisible(false);
                }}
                style={{ 
                    fontSize: '22px',
                    color: isUserIconHovered ? themeColor : 'inherit',
                    cursor: 'pointer'
                }}
                onMouseEnter={() => {
                    setIsUserIconHovered(true);
                    setIsUserHover(false);
                }}
                onMouseLeave={() => {
                    if (!isUserHover) {
                        setIsUserIconHovered(false);
                    }
                }}
            />
        );
    };

    return (
        <div className="ezheader">
            <div className='ezheader-inner'>
                <div className='ezheader-padding'>
                    <div className='header_image' onClick={() => { navigate('/index'); setIsLanguageVisible(false) }}>
                        <img src="/logo.png" alt="logo"/>
                    </div>
                    <div className='header_nav'>
                        {navList.map((item, index) => (
                            <div className="nav_item"
                                key={index}
                                style={{
                                    color: location.pathname === item.url || hoverIndex === index ? themeColor : 'inherit',
                                    fontSize: languageCode === 'zh' ? '16px' : '18px',
                                }}
                            >
                                {/* 导航内容被注释掉了 */}
                            </div>
                        ))}
                    </div>
                    <div className='header_right'>
                        <div
                            className='header_language'
                        >
                        </div>
                        <div
                            className='header_language_txt'
                            // onClick={() => showLanguage()}
                            style={{ color: isLanguageVisible || isLanguageHover ? themeColor : 'inherit' }}
                        >
                        </div>
                        <div className="header_language_right"
                            style={{
                                fontSize: "22px",
                                color: isUserIconHovered ? themeColor : 'inherit',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                            {/* 根据登录状态显示头像或图标 */}
                            {renderUserAvatarOrIcon()}
                            
                            {loginState == 1 &&
                                <div className="loginOut"
                                    onMouseEnter={() => {
                                        setIsUserHover(true);
                                        setIsUserIconHovered(true);
                                    }}
                                    onMouseLeave={() => {
                                        setIsUserHover(false);
                                        setIsUserIconHovered(false);
                                    }}
                                    style={{
                                        color: isUserHover ? themeColor : '#000',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        fontSize: '14px',
                                        opacity: isUserIconHovered ? 1 : 0,
                                        visibility: isUserIconHovered ? 'visible' : 'hidden',
                                        transform: isUserIconHovered ? 'translateY(0)' : 'translateY(-10px)'
                                    }}
                                    onClick={() => { 
                                        // 退出登录逻辑
                                        localStorage.removeItem('userToken');
                                        localStorage.removeItem('headImg');
                                        localStorage.removeItem('accountName');
                                        setLoginState(0);
                                        navigate('/login');
                                    }}>
                                    <LogoutOutlined />
                                    <span>退出登录</span>
                                </div>
                            }
                        </div>
                    </div>
                </div>
                {/* 语言选择下拉框被注释掉了 */}
            </div>
            {isLanguageVisible &&
                <div
                    className="language_overlay"
                    onClick={() => { setIsLanguageVisible(false) }}
                ></div>
            }
        </div>
    );
};

export default Header;